import type React from 'react'
import { PanelSuggestionSkeleton } from '@/components/ui/skeleton'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { PanelSuggestionsProps } from '@/types/mainPageComponents'
import { getPanelImage, getSectionModulus } from '@/utils/searchUtils'
import { formatNumber } from '@/utils/unitConversion'

export const PanelSuggestions: React.FC<PanelSuggestionsProps> = ({
  isLoading,
  suggestions,
  searchValue,
  unitDisplay,
  onSelectPanel,
  exactMatchHeaderKey,
  exactMatchKey,
  noResultsKey = 'main_page.search.by_name.no_results',
  valueType,
}) => {
  const { t } = useLanguage()

  if (isLoading) {
    return (
      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
        {Array.from({ length: 4 }).map((_, i) => (
          <PanelSuggestionSkeleton key={i} />
        ))}
      </div>
    )
  }

  if (suggestions.above.length === 0 && suggestions.exact.length === 0) {
    return (
      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
        <div className="p-4 text-center text-gray-500 text-sm">{t(noResultsKey)}</div>
      </div>
    )
  }

  return (
    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
      <div className="p-2 text-sm text-gray-600 border-b">
        {t(exactMatchHeaderKey)} {searchValue} {unitDisplay}
      </div>

      {/* Show exact matches first */}
      {suggestions.exact.map((panel) => {
        const value =
          valueType === 'bendingMoment' ? panel.bending_moment || 0 : getSectionModulus(panel)
        return (
          <button
            key={panel.id}
            type="button"
            onClick={() => onSelectPanel(panel)}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left bg-green-50 border-l-4 border-green-500"
          >
            {getPanelImage(panel) ? (
              <img
                src={getPanelImage(panel)}
                alt={panel.name}
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-600">
                {panel.name?.substring(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {panel.name}
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                  {t(exactMatchKey)}
                </span>
              </div>
              <div className="text-sm text-green-600 font-semibold">
                {formatNumber(value, 1)} {unitDisplay}
              </div>
            </div>
          </button>
        )
      })}

      {/* Show stronger panels (above) */}
      {suggestions.above.map((panel) => {
        const value =
          valueType === 'bendingMoment' ? panel.bending_moment || 0 : getSectionModulus(panel)
        return (
          <button
            key={panel.id}
            type="button"
            onClick={() => onSelectPanel(panel)}
            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
          >
            {getPanelImage(panel) ? (
              <img
                src={getPanelImage(panel)}
                alt={panel.name}
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center text-xs font-bold text-gray-600">
                {panel.name?.substring(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <div className="font-medium text-gray-900">{panel.name}</div>
              <div className="text-sm text-gray-500">
                {formatNumber(value, 1)} {unitDisplay}
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
